import type { Department, TimeEntry } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { Dialog } from "../../../elements/dialog";
import "../../../elements/entity-multi-select";
import type { EntityMultiSelect } from "../../../elements/entity-multi-select";
import "../../../elements/popover";
import { colors } from "../../../styles";

type Input = { timeEntries: TimeEntry[]; departments: Department[] };

export type CreateRosterTemplateResult = { name: string; departmentIds: number[] };

@customElement("ptc-create-roster-template-dialog")
export class CreateRosterTemplateDialog extends Dialog<Input, CreateRosterTemplateResult> {
    readonly preventDismiss = true;

    @state()
    private _availableDepartments: Department[] = [];

    @state()
    private _timeEntries: TimeEntry[] = [];

    @query("[name='departments']")
    private _departmentsInput: EntityMultiSelect<Department>;

    @query("form")
    private _form: HTMLFormElement;

    private async _submit(e: Event) {
        e.preventDefault();

        const departmentsInput = this._departmentsInput;

        const departmentIds =
            departmentsInput?.selected.map((d) => d.id) || this._availableDepartments.map((d) => d.id);
        const formData = new FormData(this._form);
        const name = (formData?.get("name") as string) || "";

        this.done({ name, departmentIds });
    }

    async show({ timeEntries, departments }: Input) {
        const promise = super.show();

        this._timeEntries = timeEntries;
        this._availableDepartments = departments;

        await this.updateComplete;

        this._departmentsInput.selected = departments.filter((d) =>
            this._timeEntries.some((e) => e.position?.departmentId === d.id)
        );

        return promise;
    }

    static styles = [...Dialog.styles, css``];

    renderContent() {
        return html`
            <div class="padded center-aligning horizontal layout border-bottom">
                <div class="half-padded semibold stretch">
                    <ptc-ui-icon name="icon.action.apply-template"></ptc-ui-icon> Vorlage Erstellen
                </div>
                <button class="slim transparent" @click=${() => this.done()}>
                    <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                </button>
            </div>
            <form class="padded spacing vertical layout" @submit=${this._submit}>
                <div class="field">
                    <label><ptc-ui-icon name="icon.object.form"></ptc-ui-icon> Name der Vorlage</label>
                    <input type="text" name="name" placeholder="Vorlagenname" required />
                </div>

                <div class="field">
                    <label><ptc-ui-icon name="icon.action.filter" class="smaller"></ptc-ui-icon> Abteilungen</label>

                    <ptc-entity-multi-select
                        name="departments"
                        .existing=${this._availableDepartments}
                        .getId=${(dep: Department) => dep.id}
                        .getLabel=${(dep: Department) =>
                            `${dep.name} (${
                                this._timeEntries.filter((entry) => entry.position?.departmentId === dep.id).length
                            })`}
                        .getColor=${(dep: Department) => colors[dep.color] || dep.color}
                        .getIcon=${() => "icon.object.department"}
                        emptyLabel="Alle Abteilungen"
                        emptyIcon="icon.object.department-group"
                        addLabel="Filter Hinzufügen..."
                        noOptionsLabel="Keine Abteilung gefunden."
                    ></ptc-entity-multi-select>
                </div>

                <div class="spacing evenly stretching horizontal layout">
                    <button class="primary">Vorlage Erstellen</button>
                </div>
            </form>
        `;
    }
}
